import React, { useEffect, useState } from 'react';
import Select, { SingleValue, StylesConfig } from 'react-select';
import { t } from 'i18next';
import TimeTaxSupportSystemClient from '../../system/TimeTaxSupportSystemClient';
import { CollaboratorData } from '../../system/collaborators/interfaces/data-grid-collaborator';
import useUserProfiles from '../hooks/useUserProfiles';
import { Nemotecnico } from '../../system/perfiles/UserProfileSystem';

interface OptionType {
  value: string;
  label: string;
}

interface CollaboratorSelectorProps {
  system: TimeTaxSupportSystemClient;
  label?: string;
  onChangeSelected: (newCollaborator?: CollaboratorData) => void;
  defaultId?: string | number;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  styles?: React.CSSProperties;
}

export const customStylesReactSelect: StylesConfig<OptionType, false> = {
  control: (provided) => ({
    ...provided,
    minHeight: 36,
    borderColor: 'rgb(96, 94, 92)',
    ':focus-within': {
      borderColor: 'rgb(0, 120, 212)',
      boxShadow: '0 0 0 1px rgb(0, 120, 212)',
    },
  }),
  menu: (provided) => ({ ...provided, zIndex: 1000 }),
  option: (base, { isSelected }) => ({
    ...base,
    ':hover': {
      background: 'rgb(243, 242, 241)',
      color: 'rgb(32, 31, 30)',
    },
    background: isSelected ? 'rgb(243, 242, 241)' : 'transparent',
    color: isSelected ? 'rgb(32, 31, 30)' : base.color,
  }),
};

const CollaboratorSelector: React.FC<CollaboratorSelectorProps> = ({
  system,
  label = t('Responsable'),
  onChangeSelected,
  defaultId,
  disabled = false,
  placeholder = t('Seleccione un colaborador...'),
  className,
  styles,
}) => {
  const [collaborators, setCollaborators] = useState<CollaboratorData[]>([]);
  const [selectedOption, setSelectedOption] = useState<SingleValue<OptionType>>(null);
  const { profiles } = useUserProfiles(system.getUserProfileSystem());

  useEffect(() => {
    if (profiles.length > 0) {
      (async () => {
        // Filtra solo los perfiles deseados que no sean stdUser
        const filteredProfilesIds = profiles.filter((p) => Nemotecnico.stdUser !== p.per_nemotecnico).map((p) => p.per_id);
        const data = await system.getCollaboratorSystem().getCollaboratorByProfiles(filteredProfilesIds, 0, 99999, true);
        setCollaborators(data.data);
      })();
    }
  }, [profiles]);

  // Inicializa la opción por defecto si defaultId viene
  useEffect(() => {
    if (defaultId && collaborators.length > 0) {
      const found = collaborators.find((c) => c.col_id === defaultId.toString());
      if (found) {
        setSelectedOption({ value: found.col_id, label: `${found.col_nombre} ${found.col_apellido}` });
        onChangeSelected(found);
      }
    }
  }, [defaultId, collaborators]);

  const options: OptionType[] = collaborators.map((c) => ({
    value: c.col_id,
    label: `${c.col_nombre} ${c.col_apellido}`,
  }));

  const handleChange = (selected: SingleValue<OptionType>) => {
    setSelectedOption(selected);
    const collaborator = collaborators.find((c) => c.col_id === selected?.value);
    onChangeSelected(collaborator);
  };

  return (
    <div className={className} style={styles}>
      <label style={{ padding: '5px 0' }}>{label}</label>
      <Select
        isDisabled={disabled || collaborators.length === 0}
        isLoading={!collaborators.length}
        options={options}
        value={selectedOption}
        noOptionsMessage={() => t('No se encontraron resultados')}
        onChange={handleChange}
        placeholder={placeholder}
        styles={customStylesReactSelect}
        isClearable
      />
    </div>
  );
};

export default CollaboratorSelector;
